import { useState } from "react";
import { P, pub } from "@/lib/publicApi";
import { L } from "@/components/public/ComplaintForm";
import { toast } from "sonner";
import { Search, Download, Send, CheckCircle2, CircleDot, Circle } from "lucide-react";

const field = "w-full px-3 py-2.5 text-sm rounded-sm border outline-none bg-white text-[#12263A] placeholder:text-[#94A3B8] focus:border-[#12263A]";

export default function CaseTracker() {
  const [caseNo, setCaseNo] = useState("");
  const [code, setCode] = useState("");
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const lookup = async () => {
    if (!caseNo.trim() || !code.trim()) { toast.error("Enter your case number and secure access code."); return; }
    setBusy(true);
    try {
      const r = await pub.get(`/cases/${encodeURIComponent(caseNo.trim())}`, { params: { code: code.trim() } });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Case lookup failed."); }
    finally { setBusy(false); }
  };
  const sendReply = async () => {
    if (reply.trim().length < 2) return;
    try {
      await pub.post(`/cases/${encodeURIComponent(caseNo.trim())}/messages`, { code: code.trim(), text: reply });
      toast.success("Message added to your case record");
      setReply("");
      lookup();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send message."); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <img src="/brand/ia-seal.png" alt="Matcom Internal Affairs seal" data-testid="casetracker-ia-seal" className="w-20 h-20 rounded-full shrink-0" />
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: P.gold }}>Matcom Internal Affairs</div>
          <h2 className="text-2xl font-bold" style={{ color: P.navy }}>Check My Case</h2>
          <p className="text-sm mt-1" style={{ color: P.dim }}>Enter the case number and secure access code from your receipt to view your case status and correspondence.</p>
        </div>
      </div>
      <div className="p-4 rounded-sm border flex flex-col sm:flex-row gap-3 sm:items-end" style={{ borderColor: P.line, background: "#fff" }}>
        <div className="flex-1"><L>Case Number</L><input data-testid="track-case-number" className={field} style={{ borderColor: P.line }} value={caseNo} onChange={(e) => setCaseNo(e.target.value)} placeholder="2026-0001" /></div>
        <div className="flex-1"><L>Secure Access Code</L><input data-testid="track-access-code" className={`${field} font-mono`} style={{ borderColor: P.line }} value={code} onChange={(e) => setCode(e.target.value)} placeholder="A1B2C3D4" /></div>
        <button data-testid="track-lookup-btn" disabled={busy} onClick={lookup} className="flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-bold text-white rounded-sm hover:opacity-90 disabled:opacity-50" style={{ background: P.navy }}><Search size={15} /> {busy ? "CHECKING…" : "CHECK STATUS"}</button>
      </div>
      {data && (
        <div data-testid="track-result" className="border rounded-sm overflow-hidden" style={{ borderColor: P.line, background: "#fff" }}>
          <div className="px-5 py-4 flex items-center justify-between flex-wrap gap-2" style={{ background: P.navy }}>
            <div>
              <div className="text-white font-bold text-lg font-mono">CASE {data.case_number}</div>
              <div className="text-xs" style={{ color: "#B9C6D2" }}>{data.kind.toUpperCase()} · Filed {new Date(data.filed_at).toLocaleDateString("en-US", { dateStyle: "long" })} · {data.categories.join(", ")}</div>
            </div>
            <button data-testid="track-receipt-pdf" onClick={() => window.open(`${pub.defaults.baseURL}/cases/${data.case_number}/receipt.pdf?code=${code.trim()}`, "_blank")}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-sm" style={{ background: P.gold, color: "#fff" }}><Download size={13} /> RECEIPT PDF</button>
          </div>
          <div className="p-5">
            <div className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: P.dim }}>Case Progress</div>
            <div className="space-y-0">
              {data.timeline.map((t, i) => (
                <div key={t.stage} data-testid={`track-stage-${t.stage}`} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    {t.state === "done" ? <CheckCircle2 size={20} style={{ color: P.green }} /> : t.state === "active" ? <CircleDot size={20} style={{ color: P.gold }} /> : <Circle size={20} style={{ color: P.line }} />}
                    {i < data.timeline.length - 1 && <div className="w-0.5 flex-1 min-h-[18px]" style={{ background: t.state === "done" ? P.green : P.line }} />}
                  </div>
                  <div className="pb-4">
                    <div className="text-sm font-semibold" style={{ color: t.state === "pending" ? P.dim : P.ink }}>{t.label}
                      {t.state === "active" && <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-sm text-white" style={{ background: P.gold }}>ACTIVE</span>}
                    </div>
                    {t.ts && <div className="text-xs" style={{ color: P.dim }}>{new Date(t.ts).toLocaleDateString("en-US", { dateStyle: "medium" })}</div>}
                  </div>
                </div>
              ))}
            </div>
            {data.final_disposition && (
              <div data-testid="track-final-disposition" className="mt-2 p-4 rounded-sm" style={{ background: "#EDF5EF", border: `1px solid ${P.green}` }}>
                <div className="text-xs font-bold uppercase tracking-wider" style={{ color: P.green }}>Final Disposition Available</div>
                <div className="text-sm mt-1" style={{ color: P.ink }}>{data.final_disposition}</div>
              </div>
            )}
            <div className="mt-5 pt-4 border-t" style={{ borderColor: P.line }}>
              <div className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: P.dim }}>Message Center</div>
              {(data.messages || []).length === 0 && <div className="text-sm italic" style={{ color: P.dim }}>No correspondence yet. Messages from the agency will appear here.</div>}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {(data.messages || []).map((m, i) => (
                  <div key={i} className="p-3 rounded-sm text-sm" style={{ background: m.from === "citizen" ? "#F1EFE8" : "#EEF2F6", border: `1px solid ${P.line}` }}>
                    <div className="text-[10px] font-bold uppercase tracking-wider mb-0.5" style={{ color: m.from === "citizen" ? P.dim : P.navy }}>{m.author || (m.from === "citizen" ? "You" : "Agency")} · {new Date(m.ts).toLocaleString()}</div>
                    <div style={{ color: P.ink }}>{m.text}</div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <input data-testid="track-reply-input" className={field} style={{ borderColor: P.line }} value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendReply()} placeholder="Add a follow-up message to your case…" />
                <button data-testid="track-reply-send" onClick={sendReply} className="px-4 rounded-sm text-white" style={{ background: P.navy }}><Send size={15} /></button>
              </div>
            </div>
            {(data.evidence || []).length > 0 && (
              <div className="mt-4 pt-4 border-t" style={{ borderColor: P.line }}>
                <div className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: P.dim }}>Submitted Evidence ({data.evidence.length})</div>
                {data.evidence.map((e) => (
                  <div key={e.eid} className="text-sm flex items-center gap-2 py-1" style={{ color: P.ink }}>
                    <span className="font-mono text-xs px-1.5 py-0.5 rounded-sm" style={{ background: "#EEF2F6", color: P.navy }}>{e.eid}</span>
                    {e.filename} <span className="text-xs" style={{ color: P.dim }}>({Math.round(e.size / 1024)} KB)</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
